import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
  KeyboardEvent,
} from 'react'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { useDocumentState } from '@/state'
import { cn, createSnapModifier } from '@/utils'
import type {
  FlattenedDocumentPage,
  TemplateAttributeTypes,
} from '@/lib/api/client'
import { useDocumentPageQuery } from '../../queries'
import { useSaveDraggableElement } from '../Annotations/queries'
import {
  Position,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Line } from '../Annotations/Line'
import {
  DEFAULT_STROKE_COLOR,
  DISABLED_STROKE_COLOR,
} from '../Annotations/constants'
import {
  isLine,
  isArrow,
  getSubPageIndexFromAbsoluteY,
  getRelativeY,
} from '../Annotations/utils'
import DocumentGrid from '../DocumentGrid'
import { useListTemplateAttributes } from '@/services/hooks/template_attributes'
import { AnnotationsProvider } from './AnnotationsContext'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import { DraggableElements } from '../DraggableElements'
import { useDeleteTemplateAttribute } from '@/services/queries/template_attributes'

const GRID_HEIGHT = 20
const GRID_WIDTH = 20

export const AnnotationsContainer = ({
  documentPage,
  isReadOnly,
  children,
}: {
  documentPage: FlattenedDocumentPage
  isReadOnly: boolean
  children?: ReactNode
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  const { data: docData } = useDocumentPageQuery()
  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId: documentPage?.id as string,
  })
  const annotationsCanvasRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const setLinePoint = useDocumentState((state) => state.setLinePoint)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const { mutate: saveElement } = useSaveDraggableElement()
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const setMultiSelectDelta = useDocumentState(
    (state) => state.setMultiSelectDelta,
  )
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const dataType = useMemo(
    () => newElementData?.dataType,
    [newElementData?.dataType],
  )
  const type = useMemo(
    () =>
      dataType === 'annotation' ? newElementData?.attributes.type : dataType,
    [dataType, newElementData?.attributes.type],
  )
  const zoom = useDocumentState((state) => state.zoom)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage?.id as string,
  })
  const { mutateAsync: deleteTemplateAttribute } = useDeleteTemplateAttribute()

  const onRemove = useCallback(
    async (event: KeyboardEvent<HTMLDivElement>) => {
      const isRemovable = selectedElement && selectedElement.isRemovable
      if (!isRemovable) {
        return
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        await deleteTemplateAttribute({
          documentPageId: selectedElement?.documentPageId as string,
          templateAttributeId: selectedElement?.id as string,
        })
      }
    },
    [deleteTemplateAttribute, selectedElement],
  )

  const addAnnotation = useCallback(
    (clickPosition: Position) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const iconPlaceholder = globalThis.document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const { width: placeholderWidth, height: placeholderHeight } =
        iconPlaceholder.getBoundingClientRect()
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      if (canDropElement) {
        saveElement({
          documentPage,
          position: {
            x:
              clickPosition.x / zoomPercentage -
              canvasRect.x / zoomPercentage -
              (placeholderWidth + 1) / zoomPercentage / 2,
            y:
              clickPosition.y / zoomPercentage -
              canvasRect.y / zoomPercentage -
              (placeholderHeight - 5) / zoomPercentage / 2,
          },
        })
      }
    },
    [
      documentPage,
      annotationsCanvasRef,
      zoomPercentage,
      newElementData,
      canDropElement,
      saveElement,
    ],
  )

  const addLine = useCallback(
    (clickPosition: { x: number; y: number }) => {
      if (!document || !annotationsCanvasRef.current || !newElementData) return
      const canvasRect = annotationsCanvasRef.current.getBoundingClientRect()
      if (!newElementData.attributes.startPoint && canDropElement) {
        setLinePoint({
          type: 'start',
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
      if (newElementData.attributes.startPoint && canDropElement) {
        saveElement({
          documentPage,
          position: {
            x: clickPosition.x / zoomPercentage - canvasRect.x / zoomPercentage,
            y: clickPosition.y / zoomPercentage - canvasRect.y / zoomPercentage,
          },
        })
      }
    },
    [
      documentPage,
      annotationsCanvasRef,
      newElementData,
      canDropElement,
      zoomPercentage,
      setLinePoint,
      saveElement,
    ],
  )

  const onAddAnnotation = useCallback(
    (event: MouseEvent) => {
      if (!newElementData) return
      if (isLine(type)) {
        addLine({ x: event.clientX, y: event.clientY })
      } else {
        addAnnotation({ x: event.clientX, y: event.clientY })
      }
    },
    [newElementData, type, addAnnotation, addLine],
  )

  useEffect(() => {
    const annotationsCanvas = annotationsCanvasRef.current

    if (isAddingElement && annotationsCanvas) {
      annotationsCanvas.addEventListener('click', onAddAnnotation)
      if (type && isLine(type)) {
        annotationsCanvas.style.cursor = 'crosshair'
      } else {
        annotationsCanvas.style.cursor = 'auto'
      }
    } else if (!isAddingElement && annotationsCanvas) {
      annotationsCanvas.removeEventListener('click', onAddAnnotation)
    }

    return () => {
      if (annotationsCanvas) {
        annotationsCanvas.removeEventListener('click', onAddAnnotation)
        annotationsCanvas.style.cursor = 'auto'
      }
    }
  }, [
    isAddingElement,
    annotationsCanvasRef,
    newElementData,
    type,
    onAddAnnotation,
  ])

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 0,
      tolerance: 5,
    },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 0,
      tolerance: 5,
    },
  })

  const sensors = useSensors(mouseSensor, touchSensor)

  const showContainerBorder = useMemo(
    () =>
      (isAddingElement &&
        newElementData?.documentPageId === documentPage?.id) ||
      isDragging,
    [isAddingElement, newElementData, documentPage, isDragging],
  )

  const snapToGrid = () => {
    if (showGrid) {
      return createSnapModifier({
        gridDimensions: {
          height: GRID_HEIGHT,
          width: GRID_WIDTH,
        },
        getGridContainer: () => gridRef.current,
        getZoom: () => zoomPercentage,
      })
    }

    return createSnapModifier({
      gridDimensions: {
        height: 1,
        width: 1,
      },
      getGridContainer: () => gridRef.current,
      getZoom: () => zoomPercentage,
    })
  }

  const bulkSelectedTemplateAttributes = useDocumentState(
    (state) => state.bulkSelectedTemplateAttributes,
  )

  const updateTemplatePosition = useCallback(
    ({
      currentTemplate,
      xPos,
      yPos,
    }: {
      currentTemplate: TemplateAttribute<any>
      xPos: number
      yPos: number
    }) => {
      const zoomedDeltaX = xPos / zoomPercentage
      const zoomedDeltaY = yPos / zoomPercentage

      const currentPosition = (currentTemplate?.template_values
        ?.position as any) || { x: 0, y: 0 }
      const currentX = currentPosition?.x ?? 0
      const currentY = currentPosition?.y ?? 0
      const documentSubPages =
        (documentPage?.template_values?.subPages as Array<string>) ?? []
      const currentSubPageId =
        currentTemplate?.template_values?.subPageId ?? null
      const currentSubPageIndex = Math.max(
        documentSubPages.findIndex((id) => id === currentSubPageId),
        0,
      )
      const absoluteY =
        (currentSubPageIndex === 0
          ? currentY
          : LETTER_PAGE_HEIGHT -
            64 +
            (currentSubPageIndex - 1) * LETTER_PAGE_HEIGHT +
            currentY) + zoomedDeltaY

      const newSubPageIndex = getSubPageIndexFromAbsoluteY(absoluteY)
      const newSubPageId = documentSubPages[newSubPageIndex] ?? currentSubPageId
      const newRelativeY = getRelativeY(absoluteY)

      const updatedTemplate = {
        data_type: currentTemplate.data_type,
        payload: {
          subPageId: newSubPageId,
          position: {
            ...(currentTemplate?.template_values?.position || {}),
            x: currentX + zoomedDeltaX,
            y: newRelativeY,
          },
        },
        templateAttributeId: currentTemplate.id as string,
        currentTemplate,
      }

      void updateTemplate(updatedTemplate)
    },
    [documentPage?.template_values?.subPages, updateTemplate, zoomPercentage],
  )

  const onDragEnd = useCallback(
    async ({ delta: { x, y }, active }) => {
      setShowGrid(false)
      if (x || y) {
        const currentTemplate = templateAttributes?.find(
          (template) => template.id === active.id,
        )

        if (currentTemplate) {
          updateTemplatePosition({
            currentTemplate,
            xPos: x,
            yPos: y,
          })
        }

        if (bulkSelectedTemplateAttributes.length > 0) {
          bulkSelectedTemplateAttributes.forEach((bulkTemplateAttribute) => {
            if (bulkTemplateAttribute.id === currentTemplate?.id) {
              return
            }

            const followerTemplateAttribute = templateAttributes?.find(
              (template) => template.id === bulkTemplateAttribute.id,
            )

            if (!followerTemplateAttribute) {
              return
            }

            updateTemplatePosition({
              currentTemplate: followerTemplateAttribute,
              xPos: x,
              yPos: y,
            })
          })
        }
      }
      setIsDragging(false)
      setMultiSelectDelta({ x: 0, y: 0 })
    },
    [
      bulkSelectedTemplateAttributes,
      setMultiSelectDelta,
      templateAttributes,
      updateTemplatePosition,
    ],
  )

  if (
    !docData ||
    !docData.project ||
    !docData.version ||
    !docData.documentPages
  ) {
    return null
  }

  return (
    <AnnotationsProvider
      setIsResizing={setIsDragging}
      annotationsCanvasRef={annotationsCanvasRef}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[snapToGrid()]}
        onDragStart={({ active }) => {
          setMultiSelectDelta({ x: 0, y: 0 })
          const draggableId = active.id as string
          const dataType = active.data.current?.type as TemplateAttributeTypes

          setSelectedElement(null)
          setSelectedElement({
            documentPageId: documentPage.id as string,
            id: draggableId,
            isRemovable: true,
            type: dataType,
          })
        }}
        onDragMove={({ active, delta }) => {
          const { type } = (active.data?.current as any) || {}
          setIsDragging(true)
          setMultiSelectDelta(delta)

          if (type !== 'annotation') {
            setShowGrid(true)
          }
        }}
        onDragEnd={onDragEnd}
        autoScroll={false}
        onDragCancel={() => {
          setIsDragging(false)
          setMultiSelectDelta({ x: 0, y: 0 })
        }}
      >
        <div
          className={cn(
            'flex flex-col rounded border flex-1 mb-9 relative justify-between',
            {
              'border-transparent': !showContainerBorder,
              'border-solid': showContainerBorder,
              'border-primary-40': showContainerBorder,
              'shadow-md': showContainerBorder,
              'shadow-gray-400': showContainerBorder,
            },
          )}
          ref={annotationsCanvasRef}
          id={`droppable-container--${documentPage.id}`}
          onKeyDown={(e) => void onRemove(e)}
        >
          <DocumentGrid
            gridDimensions={{
              width: GRID_WIDTH,
              height: GRID_HEIGHT,
            }}
            showGrid={showGrid}
            ref={gridRef}
          />
          {isAddingElement &&
            newElementData?.documentPageId === documentPage.id &&
            isLine(type || '') &&
            newElementData?.attributes.startPoint && (
              <div className="z-[2] absolute">
                <Line
                  startPoint={newElementData?.attributes.startPoint}
                  endPoint={
                    newElementData?.attributes.endPoint ||
                    newElementData?.attributes.startPoint
                  }
                  strokeOpacity={canDropElement ? 0.6 : 1}
                  strokeColor={
                    canDropElement
                      ? newElementData?.attributes.strokeColor ||
                        DEFAULT_STROKE_COLOR
                      : DISABLED_STROKE_COLOR
                  }
                  isArrow={isArrow(type || '')}
                  isBubble={(type && type?.includes('bubble')) || false}
                  bubbleValue={newElementData?.attributes.bubbleValue}
                  renderWrapper={true}
                  textPosition={type?.split('--')[1] || ''}
                />
              </div>
            )}
          <DraggableElements
            isReadOnly={isReadOnly}
            documentPage={documentPage}
            onNudgeStart={(showGrid: boolean) => {
              if (showGrid) {
                setShowGrid(true)
              }
              setIsDragging(true)
            }}
            onNudgeEnd={(hideGrid: boolean) => {
              if (hideGrid) {
                setShowGrid(false)
              }
              setIsDragging(false)
            }}
          />
          {children}
        </div>
      </DndContext>
    </AnnotationsProvider>
  )
}
